import { useScrollReveal } from '../../hooks/useScrollReveal';

const STEPS = [
  {
    number: '01',
    icon: 'ri-phone-line',
    title: 'Contact Us',
    desc: 'Reach our GTA West or East team by phone — 24/7 for emergencies. We respond quickly and book your appointment at a time that suits you.',
  },
  {
    number: '02',
    icon: 'ri-home-4-line',
    title: 'Free In-Home Assessment',
    desc: 'A T.S.S.A.-approved technician visits your home at no charge — evaluating your system, space, and specific comfort requirements in full.',
  },
  {
    number: '03',
    icon: 'ri-draft-line',
    title: 'Honest Recommendation',
    desc: 'We present clear options with upfront pricing. No pressure, no upselling, no hidden fees — just the right solution for your home and budget.',
  },
  {
    number: '04',
    icon: 'ri-tools-line',
    title: 'Expert Installation',
    desc: 'Our certified technicians complete your installation with precision — protecting your home throughout and working efficiently to minimize disruption.',
  },
  {
    number: '05',
    icon: 'ri-checkbox-circle-line',
    title: 'Quality Follow-Up',
    desc: 'We follow up after every job to confirm your system performs perfectly. Your complete satisfaction is the standard we hold ourselves to.',
  },
];

export default function ProcessSection() {
  const { ref, visible } = useScrollReveal<HTMLElement>();

  return (
    <section ref={ref} className="relative py-28 lg:py-36 overflow-hidden bg-luxury-warm">
      {/* Decorative background texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{ backgroundImage: 'radial-gradient(circle at 15% 25%, rgba(26,101,224,0.05) 0%, transparent 45%), radial-gradient(circle at 85% 75%, rgba(26,101,224,0.05) 0%, transparent 45%)' }}
      />

      <div className="max-w-[1500px] mx-auto px-6 lg:px-14 relative">

        {/* Header */}
        <div
          className={`flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-0.5 bg-luxury-gold-dark" />
              <span className="font-caps text-[10px] tracking-[0.3em] uppercase text-luxury-text-muted">How It Works</span>
            </div>
            <h2 className="font-serif text-5xl lg:text-7xl text-luxury-text-dark leading-[1.0]">
              Our Proven<br />
              <em className="not-italic text-luxury-gold-dark">Process</em>
            </h2>
          </div>
          <div className="max-w-xs">
            <p className="font-sans text-sm text-luxury-text-muted leading-relaxed">
              Five steps from first call to complete satisfaction — every job, every time, without exception.
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
          {STEPS.map((step, i) => (
            <div
              key={step.number}
              className={`group relative transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: visible ? `${i * 120}ms` : '0ms' }}
            >
              {/* Connector line (desktop) */}
              {i < STEPS.length - 1 && (
                <div className="hidden lg:block absolute top-[40px] left-[calc(50%+40px)] right-[-50%] h-px"
                  style={{ background: 'linear-gradient(90deg, rgba(26,101,224,0.30), rgba(26,101,224,0.08))' }} />
              )}

              <div className="bg-white rounded-2xl p-7 border border-luxury-gold/12 group-hover:border-luxury-gold/35 group-hover:-translate-y-1 transition-all duration-300 shadow-card h-full flex flex-col">
                {/* Step circle */}
                <div className="relative mb-6">
                  <div className="w-[72px] h-[72px] flex flex-col items-center justify-center rounded-full border-2 border-luxury-gold/22 group-hover:border-luxury-gold/55 bg-luxury-warm group-hover:bg-luxury-gold/10 transition-all duration-300">
                    <div className="w-7 h-7 flex items-center justify-center mb-0.5">
                      <i className={`${step.icon} text-luxury-gold-dark text-xl group-hover:scale-110 transition-transform duration-300`} />
                    </div>
                    <span className="font-caps text-[8px] tracking-widest text-luxury-gold/60 font-bold">{step.number}</span>
                  </div>
                </div>

                <h3 className="font-serif text-luxury-text-dark text-lg font-semibold mb-3 leading-snug group-hover:text-luxury-gold-dark transition-colors">
                  {step.title}
                </h3>
                <p className="font-sans text-sm text-luxury-text-muted leading-relaxed flex-1">
                  {step.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className={`mt-16 text-center transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: '620ms' }}
        >
          <a
            href="tel:9058454009"
            className="inline-flex items-center gap-3 bg-luxury-gold hover:bg-luxury-gold-light text-white font-caps font-semibold text-xs tracking-[0.15em] uppercase px-10 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap shadow-gold"
          >
            <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-line text-sm" /></div>
            <span>Start With a Free Consultation</span>
          </a>
        </div>
      </div>
    </section>
  );
}
